import type { CallRule, CounterView, Ticket } from './types'

// Which customer a counter marks as next, which rows of its line it draws, and
// when it has to ask the server again.
//
// The server names the next customer on the desk's view (CounterView.nextTicketId)
// with the selection Call next makes. These are the three places the screen could
// still get that wrong on its own: marking the wrong row, not drawing the row at
// all, and holding on to an answer the clock has overtaken.

/**
 * Whether this ticket is the one Call next would take at this desk, and the badge
 * should say so.
 *
 * Only the server's answer counts. With none — nobody callable, or a view from
 * before the field existed — nobody is marked: guessing from the order of the line
 * is the mistake this replaced. Nor while the desk has somebody in front of it,
 * where "next" is a promise about a call the desk cannot make yet.
 */
export function isNextHere(
  ticket: Pick<Ticket, 'id'>,
  desk: Pick<CounterView, 'nextTicketId'> | undefined,
  occupied: boolean,
): boolean {
  if (occupied || !desk?.nextTicketId) return false
  return ticket.id === desk.nextTicketId
}

/**
 * The rows of a line a counter draws: the first `limit`, and the next customer too
 * when they fall below them.
 *
 * The line is ordered by the calling clock, and under priority, queue order, fair
 * share or a condition the server can choose somebody further down. Cutting the
 * list at twelve then hid the one row the badge belongs on, and a clerk saw no
 * NEXT at all. The next customer is added at the end rather than moved up, so the
 * order still reads as the order of the wait.
 */
export function rowsShown(line: readonly Ticket[], nextTicketId: string | undefined, limit: number): Ticket[] {
  const shown = line.slice(0, limit)
  if (!nextTicketId || shown.some((t) => t.id === nextTicketId)) return shown
  const next = line.find((t) => t.id === nextTicketId)
  return next ? [...shown, next] : shown
}

/** The moment already asked about, and when the asking was done. */
export interface NextCheckAsked {
  at: string
  sentAt: number
}

/** How long to wait before asking about the same moment again. */
export const NEXT_CHECK_RETRY_MS = 5_000

/**
 * Whether the desk's answer about who is next has been overtaken by the clock and
 * should be asked for again.
 *
 * nextCheckAt is when the server said a customer this desk could call comes out of
 * a recycle cooldown or a clerk's delay. Nothing is published at that moment, so
 * the screen asks. One moment is asked about once, normally: the snapshot that
 * answers carries a later one, or none. It is asked about again after a few
 * seconds only if the answer still names it — which is what a screen whose clock
 * runs ahead of the server's gets, having asked before the server's moment came.
 */
export function nextCheckDue(
  desk: Pick<CounterView, 'nextCheckAt'> | undefined,
  now: number,
  asked: NextCheckAsked | undefined,
): boolean {
  const at = desk?.nextCheckAt
  if (!at) return false
  const when = Date.parse(at)
  if (!Number.isFinite(when) || now < when) return false
  if (asked && asked.at === at && now - asked.sentAt < NEXT_CHECK_RETRY_MS) return false
  return true
}

/**
 * Who Call next could take at this desk: the callable line, and the set-aside
 * customers this desk may bring back (its desk pool, the person's own pool — a
 * customer back from the cashier among them).
 *
 * The line alone was the count once, and a desk with an empty line and its own
 * customer set aside said "Nobody waiting" and hid Call next, though the server's
 * Call next takes that customer first. When the server names somebody next the
 * desk is never shown as empty, whatever the screen's own count: it is the server
 * that calls.
 */
export function waitingHere(
  line: readonly Pick<Ticket, 'id'>[],
  mineAside: readonly Pick<Ticket, 'id'>[],
  nextTicketId: string | undefined,
): Pick<Ticket, 'id'>[] {
  const all = [...mineAside, ...line.filter((t) => !mineAside.some((m) => m.id === t.id))]
  if (nextTicketId && !all.some((t) => t.id === nextTicketId)) return [...all, { id: nextTicketId }]
  return all
}

/** What an empty Call next says: nobody at all, or people waiting whom no desk may call
 *  yet (a recycle's cooldown, a booking not due). */
export function emptyCallKey(waiting: number): 'counter.nobodyWaiting' | 'counter.notCallableYet' {
  return waiting > 0 ? 'counter.notCallableYet' : 'counter.nobodyWaiting'
}

/**
 * Whether Call next at this desk may reach the customer at all, by who they are
 * booked with (server queue.byPrimary): a desk with no rules is a simple desk,
 * which leaves a colleague's booked customer to that colleague (`both`).
 */
export function reachableHere(
  ticket: Pick<Ticket, 'primaryStaffId'>,
  rules: readonly Pick<CallRule, 'primaryResource'>[] | undefined,
  staffId: string,
): boolean {
  const booked = ticket.primaryStaffId ?? ''
  const modes = rules && rules.length > 0 ? rules.map((r) => r.primaryResource ?? 'ignore') : ['both']
  return modes.some((m) => {
    if (m === 'with') return booked !== '' && booked === staffId
    if (m === 'without') return booked === ''
    if (m === 'both') return booked === '' || booked === staffId
    return true
  })
}
